import i18n from "@/i18n";
/**
 * ScheduleGrid — Prompt 12E §14-§30, extended by Prompt 12G-3 (direct
 * quick-edit surface) and Prompt 12I-2 (live Multi-Shift Editor
 * integration).
 *
 * Rendering remains driven entirely by the canonical Schedule
 * response. This component never calls the API — it only forwards
 * commit / delete intents upward and reads pending state from the
 * parent.
 *
 * Prompt 12I-2 attaches the completed local MultiShiftEditor to live
 * cells:
 *   • zero-Shift applicable cell: Prompt-12G ShiftCellEditor (primary
 *     first-Shift workflow).
 *   • one-Shift applicable cell: Prompt-12G ShiftCellEditor as the
 *     primary click surface PLUS a compact contextual •••
 *     `Manage shifts` action that opens MultiShiftEditor.
 *   • 2+ Shift applicable cell: no direct quick editor; the visible
 *     cell body itself is the MultiShiftEditor trigger.
 *   • N/A: no editor.
 * All Multi-Shift mutations reuse the existing Prompt-12G intent
 * contract (mode="create"|"update"|delete-with-shift) so they flow
 * through the same WorkforceSchedule mutation owner.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { AbsenceLabel, CellAbsenceControls } from "@/components/workforce/AbsenceChip";
import { formatVVMinutes, vvContextMessage } from "@/components/workforce/VVContextNote";
import { MoreHorizontal } from "lucide-react";
import {
    formatDurationMinutes,
    formatScheduleDateHeader,
} from "@/components/workforce/workforceHelpers";
import { ShiftCellEditor } from "@/components/workforce/ShiftCellEditor";
import { MultiShiftEditor } from "@/components/workforce/MultiShiftEditor";
// Prompt 12K-3 §3 · §6 · §9 — approved K2 editor components. They
// stay unchanged; ScheduleGrid mounts them for the correct canonical
// cell states and forwards local intents upward as-is.
import { PlanCellEditor } from "@/components/workforce/PlanCellEditor";
import { DayEntryCellEditor } from "@/components/workforce/DayEntryCellEditor";
import { TransferredXControl } from "@/components/workforce/TransferredXControl";
import {
    rulesForEmployee, rulesForCell, cellHasRulesSignal,
} from "@/components/workforce/workforceRules";
import { fillRange } from "@/components/workforce/shiftFill";

// Sticky Employee column classes — shared across header and body.
// Prompt PREVIEW-FIX-02-P1 — a solid right border replaces the hair
// shadow so horizontal scrolling shows a clear column separator.
const _STICKY = "sticky left-0 z-10 bg-white border-r border-neutral-200";

// Prompt 12G-4 §6 · §54 — collision-safe cell key. Identical
// convention used by WorkforceSchedule so mutation context, DOM
// traversal and now Multi-Shift open state share one identity.
function _cellKey(employeeId, date) {
    return JSON.stringify([employeeId, date]);
}

export default function ScheduleGrid({
    dates,
    employees,
    onShiftCommitIntent,
    onShiftDeleteIntent,
    onPlanCellIntent,
    isShiftCellPending,
    cancelEditVersion,
    focusRequest,
    mode,
    onReviewCellClick,
    rules,
    actualEntries,
    getTransferContextForEmployee,
    transferRestaurantId,
    // Prompt 17B2 §6 · §7 — closed-period read-only mode. When true
    // every editing affordance in the grid is suppressed and cells
    // render only their canonical body (Shift + Day Code display).
    // Reads remain visible (§7).
    readOnly,
    // Prompt 18B1 §12 · §13 · §15 — bounded per-employee absence
    // map (employee_id → [absence rows]) for the visible period.
    // Each cell derives its own intersecting absences in memory.
    absencesByEmployee,
    vvContextByEmployee,
    // CALENDAR-ABSENCES-V1 — manager-only absence actions (undefined = read-only labels).
    onAbsenceOpen,
    // Drag-fill: (employeeId, sourceDate, targetDates[]) — parent owns all writes.
    onFillIntent,
    // Copy last week (per employee row) — parent opens the dialog.
    onLastWeekCopyIntent,
}) {
    // Prompt 13C §5 — controlled mode contract. Default is "plan"
    // so existing Schedule behavior is 100% unchanged.
    const activeMode = mode === "review" ? "review" : "plan";
    // Prompt 12G-2 §17 — positional week grouping over backend dates.
    const week1 = dates.slice(0, 7);
    const week2 = dates.slice(7, 14);
    const week3 = dates.slice(14, 21);

    // Prompt 12G-4 §27 · §35 · §43 — one-shot focus request consumer.
    const gridRootRef = useRef(null);
    const lastFocusReqIdRef = useRef(null);

    // Prompt 12I-2 §10 · §11 — single contextual editor open key.
    const [openMultiShiftCellKey, setOpenMultiShiftCellKey] = useState(null);

    // Prompt 12I-2 §12 · §52 — cancelEditVersion changes clear any
    // open contextual editor so old Employee/date state cannot leak
    // into a new Period / Location.
    const cancelSeenRef = useRef(cancelEditVersion);
    useEffect(() => {
        if (cancelSeenRef.current === cancelEditVersion) return;
        cancelSeenRef.current = cancelEditVersion;
        setOpenMultiShiftCellKey(null);
    }, [cancelEditVersion]);

    // Drag-fill state: { employeeId, sourceDate, targetDate } while dragging.
    const [fill, setFill] = useState(null);
    const fillRef = useRef(null);
    const startFill = useCallback((employeeId, sourceDate) => {
        const _set = (v) => { fillRef.current = v; setFill(v); };
        _set({ employeeId, sourceDate, targetDate: sourceDate });
        const onMove = (e) => {
            const el = document.elementFromPoint(e.clientX, e.clientY);
            const td = el && el.closest("td[data-fill-date]");
            const cur = fillRef.current;
            if (!td || !cur || td.getAttribute("data-fill-employee") !== cur.employeeId) return;
            const d = td.getAttribute("data-fill-date");
            if (d !== cur.targetDate) _set({ ...cur, targetDate: d });
        };
        const onUp = () => {
            window.removeEventListener("pointermove", onMove);
            window.removeEventListener("pointerup", onUp);
            const cur = fillRef.current;
            _set(null);
            const targets = cur ? fillRange(dates, cur.sourceDate, cur.targetDate) : [];
            if (targets.length && typeof onFillIntent === "function") {
                onFillIntent(cur.employeeId, cur.sourceDate, targets);
            }
        };
        window.addEventListener("pointermove", onMove);
        window.addEventListener("pointerup", onUp);
    }, [dates, onFillIntent]);
    const fillTargets = fill ? new Set(fillRange(dates, fill.sourceDate, fill.targetDate)) : null;

    const handleMultiOpenChange = useCallback((cellKey, nextOpen) => {
        setOpenMultiShiftCellKey((cur) => {
            if (nextOpen) return cellKey;
            // Only clear if this cell is the one currently open.
            return cur === cellKey ? null : cur;
        });
    }, []);

    useEffect(() => {
        if (!focusRequest) return;
        if (lastFocusReqIdRef.current === focusRequest.request_id) return;
        lastFocusReqIdRef.current = focusRequest.request_id;
        const root = gridRootRef.current;
        if (!root) return;
        // Prompt 12K-3 §28 — reuse the existing Schedule cell
        // ordering: DOM order across the grid. Broadened to also
        // include K2 wrappers so Plan-Cell and Day-Entry Tab /
        // Shift+Tab focus movement uses the same one algorithm.
        const wrappers = Array.from(root.querySelectorAll(
            "[data-workforce-shift-cell-key], "
                + "[data-workforce-plan-cell-key], "
                + "[data-workforce-day-entry-cell-key]",
        ));
        const _keyOf = (el) => (
            el.getAttribute("data-workforce-shift-cell-key")
                || el.getAttribute("data-workforce-plan-cell-key")
                || el.getAttribute("data-workforce-day-entry-cell-key")
        );
        const idx = wrappers.findIndex(
            (el) => _keyOf(el) === focusRequest.cell_key,
        );
        let target = null;
        if (focusRequest.direction === "next") {
            if (idx >= 0) {
                target = wrappers[idx + 1] || null;
            } else {
                target = wrappers.find(
                    (el) => _keyOf(el) > focusRequest.cell_key,
                ) || null;
            }
        } else if (focusRequest.direction === "previous") {
            if (idx >= 0) {
                target = wrappers[idx - 1] || null;
            } else {
                let lastLess = null;
                for (const el of wrappers) {
                    if (_keyOf(el) < focusRequest.cell_key) {
                        lastLess = el;
                    } else {
                        break;
                    }
                }
                target = lastLess;
            }
        }
        if (target && typeof target.focus === "function") {
            target.focus();
        } else if (
            root.contains(document.activeElement)
                && document.activeElement !== document.body
        ) {
            document.activeElement.blur();
        }
    }, [focusRequest]);

    return (
        <div
            ref={gridRootRef}
            data-testid="workforce-schedule-scroll"
            className="overflow-x-auto rounded-md border border-neutral-200 bg-white"
        >
            <table
                data-testid="workforce-schedule-grid"
                className="min-w-full text-sm border-collapse"
            >
                <thead className="bg-neutral-50 text-neutral-500">
                    <tr data-testid="workforce-schedule-header-top">
                        <th
                            rowSpan={2}
                            data-testid="workforce-schedule-header-employee"
                            className={`${_STICKY} text-left font-medium
                                        px-4 py-2 border-b
                                        border-neutral-200 whitespace-nowrap`}
                        >
                            {i18n.t("wf.scheduleGrid.employee", "Employee")}
                        </th>
                        <th
                            colSpan={7}
                            data-testid="workforce-schedule-header-week-1"
                            className="text-center font-medium px-2 py-2
                                        border-b border-neutral-200"
                        >
                            {i18n.t("wf.scheduleGrid.weekN", "Week {{n}}", { n: 1 })}
                        </th>
                        <th
                            colSpan={7}
                            data-testid="workforce-schedule-header-week-2"
                            className="text-center font-medium px-2 py-2
                                        border-b border-neutral-200"
                        >
                            {i18n.t("wf.scheduleGrid.weekN", "Week {{n}}", { n: 2 })}
                        </th>
                        <th
                            colSpan={7}
                            data-testid="workforce-schedule-header-week-3"
                            className="text-center font-medium px-2 py-2
                                        border-b border-neutral-200"
                        >
                            {i18n.t("wf.scheduleGrid.weekN", "Week {{n}}", { n: 3 })}
                        </th>
                        <th
                            rowSpan={2}
                            data-testid="workforce-schedule-header-hours"
                            className="text-right font-medium px-4 py-2
                                        border-b border-neutral-200
                                        whitespace-nowrap"
                        >
                            {i18n.t("wf.scheduleGrid.hours", "Hours")}
                        </th>
                        <th
                            rowSpan={2}
                            data-testid="workforce-schedule-header-balance"
                            className="text-right font-medium px-4 py-2
                                        border-b border-neutral-200
                                        whitespace-nowrap"
                        >
                            {i18n.t("wf.scheduleGrid.balance", "Balance")}
                        </th>
                    </tr>
                    <tr data-testid="workforce-schedule-header-dates">
                        {dates.map((d) => {
                            const p = formatScheduleDateHeader(d);
                            return (
                                <th
                                    key={d}
                                    data-testid={`workforce-schedule-date-${d}`}
                                    className="px-2 py-2 border-b
                                                border-neutral-200
                                                whitespace-nowrap
                                                text-xs font-medium
                                                text-neutral-500"
                                >
                                    <div>{p ? p.weekday : ""}</div>
                                    <div className="text-neutral-700">
                                        {p ? `${p.day} ${p.month}` : ""}
                                    </div>
                                </th>
                            );
                        })}
                    </tr>
                </thead>
                <tbody>
                    {employees.map((row) => (
                        <tr
                            key={row.employee_id}
                            data-testid={`workforce-schedule-row-${row.employee_id}`}
                            className="border-b border-neutral-200
                                        last:border-0 hover:bg-neutral-50/60
                                        [&>td.sticky]:hover:bg-neutral-50"
                        >
                            <td
                                data-testid={`workforce-schedule-row-employee-${row.employee_id}`}
                                className={`${_STICKY} px-4 py-2
                                            whitespace-nowrap
                                            text-neutral-900 align-top`}
                            >
                                <div className="leading-tight">
                                    {row.first_name} {row.last_name}
                                </div>
                                {row.main_role ? (
                                    <div
                                        data-testid={`workforce-schedule-row-role-${row.employee_id}`}
                                        className="text-xs text-neutral-500
                                                    leading-tight mt-0.5"
                                    >
                                        {row.main_role}
                                    </div>
                                ) : null}
                                {typeof onLastWeekCopyIntent === "function" ? (
                                    <button
                                        type="button"
                                        data-testid={`workforce-schedule-row-copy-last-week-${row.employee_id}`}
                                        onClick={() => onLastWeekCopyIntent(row)}
                                        className="mt-1 text-[11px] leading-tight text-neutral-500
                                                    underline-offset-2 hover:text-neutral-900
                                                    hover:underline transition-colors"
                                    >
                                        {i18n.t("wf.copyLastWeek.rowAction", "Copy last week")}
                                    </button>
                                ) : null}
                            </td>
                            {row.cells.map((cell) => (
                                activeMode === "review" ? (
                                    <ReviewCell
                                        key={cell.date}
                                        employeeId={row.employee_id}
                                        cell={cell}
                                        onClick={onReviewCellClick}
                                        rules={rules}
                                        actualEntries={actualEntries}
                                        readOnly={readOnly}
                                        absencesForEmployee={
                                            (absencesByEmployee || {})[
                                                row.employee_id] || []}
                                        vvContext={vvContextByEmployee?.[
                                            row.employee_id]}
                                        onAbsenceOpen={onAbsenceOpen}
                                    />
                                ) : (
                                    <ScheduleCell
                                        key={cell.date}
                                        employee={row}
                                        cell={cell}
                                        onShiftCommitIntent={onShiftCommitIntent}
                                        onShiftDeleteIntent={onShiftDeleteIntent}
                                        onPlanCellIntent={onPlanCellIntent}
                                        isShiftCellPending={isShiftCellPending}
                                        cancelEditVersion={cancelEditVersion}
                                        openMultiShiftCellKey={openMultiShiftCellKey}
                                        onMultiOpenChange={handleMultiOpenChange}
                                        getTransferContextForEmployee={getTransferContextForEmployee}
                                        transferRestaurantId={transferRestaurantId}
                                        readOnly={readOnly}
                                        absencesForEmployee={
                                            (absencesByEmployee || {})[
                                                row.employee_id] || []}
                                        vvContext={vvContextByEmployee?.[
                                            row.employee_id]}
                                        onAbsenceOpen={onAbsenceOpen}
                                        onFillStart={typeof onFillIntent === "function"
                                            ? startFill : undefined}
                                        fillMark={Boolean(fill && fill.employeeId === row.employee_id
                                            && fillTargets.has(cell.date))}
                                    />
                                )
                            ))}
                            <td
                                data-testid={`workforce-schedule-row-hours-${row.employee_id}`}
                                className="px-4 py-2 text-right
                                            whitespace-nowrap
                                            text-neutral-700"
                            >
                                {formatDurationMinutes(row.planned_minutes)}
                            </td>
                            <td
                                data-testid={`workforce-schedule-row-balance-${row.employee_id}`}
                                className="px-4 py-2 text-right
                                            whitespace-nowrap
                                            text-neutral-400"
                            >
                                —
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            <span className="hidden" data-testid="workforce-schedule-week-1-dates">
                {week1.join(",")}
            </span>
            <span className="hidden" data-testid="workforce-schedule-week-2-dates">
                {week2.join(",")}
            </span>
            <span className="hidden" data-testid="workforce-schedule-week-3-dates">
                {week3.join(",")}
            </span>
        </div>
    );
}


// Prompt 12E §22 · §23 — canonical read-only cell body.
// Prompt 18B1 §12 · §13 · §14 — additive absence chip render.
// Absence context is displayed alongside Shift / Day Code
// facts, NEVER replacing them.
// CLOCK C3 — temporary support day here / neutral "working elsewhere"
// (no times or restaurant of the other schedule are ever shown).
function CellFlags({ employeeId, cell }) {
    return (
        <>
            {cell.support && (
                <div data-testid={`workforce-schedule-support-${employeeId}-${cell.date}`}
                    className="text-[10px] uppercase tracking-wide text-sky-700">{i18n.t("wf.scheduleGrid.support", "Support")}</div>
            )}
            {cell.unavailable_elsewhere && (
                <div data-testid={`workforce-schedule-elsewhere-${employeeId}-${cell.date}`}
                    className="text-[10px] text-amber-700">{i18n.t("wf.scheduleGrid.atAnotherRestaurant", "At another restaurant")}</div>
            )}
        </>
    );
}

function CellBody({ employeeId, cell, absences }) {
    const hasShift = cell.shifts.length > 0;
    return (
        <>
            <CellFlags employeeId={employeeId} cell={cell} />
            {cell.shifts.map((s) => (
                <div
                    key={s.schedule_entry_id}
                    data-testid={`workforce-schedule-shift-${s.schedule_entry_id}`}
                    className="text-xs leading-5"
                >
                    {s.start_time}–{s.end_time}
                </div>
            ))}
            {(absences || []).map((a) => (
                <AbsenceLabel
                    key={a.absence_id}
                    absence={a}
                    hasShift={hasShift}
                    testId={`workforce-schedule-absence-${employeeId}-${cell.date}`}
                    warnTestId={`workforce-schedule-absence-shift-warning-${employeeId}-${cell.date}`}
                />
            ))}
            {cell.day_entries.map((d) => {
                const isTransferred = Boolean(
                    d.x_transfer_source_period_id
                        && d.x_transfer_averaging_cycle_id);
                return (
                    <span
                        key={d.day_entry_id}
                        data-testid={`workforce-schedule-code-${d.day_entry_id}`}
                        data-transferred-x={
                            isTransferred ? "true" : undefined}
                        className="inline-block rounded bg-neutral-100
                                    text-neutral-700 text-xs px-1.5 py-0.5
                                    mr-1"
                    >
                        {d.code}
                        {isTransferred ? (
                            <sup
                                className="ml-0.5 text-[9px]
                                            text-neutral-600"
                                data-testid={
                                    `workforce-schedule-transferred-marker-${d.day_entry_id}`}
                                aria-label={i18n.t("wf.scheduleGrid.transferred", "Transferred")}
                                title={i18n.t("wf.scheduleGrid.transferred", "Transferred")}
                            >
                                T
                            </sup>
                        ) : null}
                    </span>
                );
            })}
            <span className="hidden" data-testid={`workforce-schedule-cell-body-${employeeId}-${cell.date}`} />
        </>
    );
}


// Prompt 13C §5 · §6 — read-only Review-mode cell. Displays
// Planned / Actual / Δ with restaurant-facing durations. Never
// invents Day-Code numeric hours. Non-reviewable cells (completely
// empty employee/day) render blank and are non-interactive.
function _fmtDur(mins) {
    if (mins == null) return "—";
    const abs = Math.abs(mins);
    const h = Math.floor(abs / 60);
    const m = abs % 60;
    return `${h} h ${String(m).padStart(2, "0")} min`;
}
function _fmtDiff(mins) {
    if (mins == null) return "—";
    if (mins === 0) return i18n.t("wf.scheduleGrid.0H00Min", "±0 h 00 min");
    return (mins > 0 ? "+" : "−") + _fmtDur(mins);
}

// TES-B2C1B2 — planned-VV facts from the backend (no VV math here).
function ReviewVVLine({ employeeId, cell }) {
    const vv = cell.vv;
    const tid = `workforce-review-vv-${employeeId}-${cell.date}`;
    if (vv.worked_on_planned_vv_day) {
        return (
            <div data-testid={tid} data-vv-state="worked"
                className="text-[10px] text-neutral-700 mt-0.5">
                {i18n.t("wf.scheduleGrid.workedOnPlannedVvDay", "Worked on planned VV day")}
            </div>
        );
    }
    if (vv.leave_credit_minutes != null) {
        return (
            <div data-testid={tid} data-vv-state="leave"
                className="text-[10px] text-neutral-700 mt-0.5">
                {i18n.t("wf.scheduleGrid.vvLeave", "VV leave")} · {formatVVMinutes(vv.leave_credit_minutes)}
            </div>
        );
    }
    const msg = vvContextMessage({ status: vv.status, vv_system: vv.vv_system });
    return (
        <div data-testid={tid} data-vv-state="context"
            className="text-[10px] text-neutral-500 mt-0.5">
            {msg ? msg.text : "VV"}
        </div>
    );
}

// Published shift elsewhere = planned work awaiting that restaurant's review.
const _DAY_OFF_PENDING_VIEW = {
    get PUBLISHED_ASSIGNMENT_NEEDS_REVIEW() { return ["amber", i18n.t("wf.scheduleGrid.publishedAssignmentNeedsReview", "Published assignment needs review")]; },
};

const _CLOCK_STATUS_VIEW = {
    get MATCH() { return ["green", i18n.t("wf.scheduleGrid.matchesPlan", "Matches plan")]; },
    get DIFFERENT() { return ["red", i18n.t("wf.scheduleGrid.clockDiffersFromPlan", "Clock differs from plan")]; },
    get INCOMPLETE() { return ["amber", i18n.t("wf.scheduleGrid.clockIncomplete", "Clock incomplete")]; },
    get UNRESOLVED() { return ["red", i18n.t("wf.scheduleGrid.clockNeedsReview", "Clock needs review")]; },
    get MEAL_MISSING() { return ["amber", i18n.t("wf.scheduleGrid.mealAnswerMissing", "Meal answer missing")]; },
};

function ReviewCell({
    employeeId, cell, onClick, rules, actualEntries, readOnly,
    absencesForEmployee, onAbsenceOpen,
}) {
    // Prompt 18B2 §2 · §3 · §4 · §5 · §6 · §7 · §8 · §9 · §11 —
    // ACTIVE absences whose inclusive range covers this cell's
    // date. Rendered as compact context beside Planned / Actual /
    // Difference / Verification WITHOUT altering any of those
    // numbers, replacing Clock evidence, or auto-verifying
    // anything. Raw absence enums NEVER shown (§2 · §21). Numeric
    // absence hours NEVER shown (§8 · §26 · never invent facts).
    // Rendered on CLOSED periods too (§11).
    const cellAbsences = (absencesForEmployee || []).filter(
        (a) => a.start_date <= cell.date && cell.date <= a.end_date);
    const cellHasShift = Array.isArray(cell.shift_ids) && cell.shift_ids.length > 0;
    let _absenceChip = null;
    if (typeof onAbsenceOpen === "function") {
        _absenceChip = (
            <CellAbsenceControls view="review" employeeId={employeeId} date={cell.date}
                absences={cellAbsences} hasShift={cellHasShift}
                canAdd={!readOnly && cell.applicable} onOpen={onAbsenceOpen} />
        );
    } else if (cellAbsences.length > 0) {
        _absenceChip = (
            <div
                className="mt-0.5 space-y-0.5"
                data-testid={
                    `workforce-review-absences-${employeeId}-${cell.date}`}
            >
                {cellAbsences.map((a) => (
                    <AbsenceLabel
                        key={a.absence_id}
                        absence={a}
                        hasShift={cellHasShift}
                        testId={`workforce-review-absence-${employeeId}-${cell.date}`}
                        warnTestId={`workforce-review-absence-shift-warning-${employeeId}-${cell.date}`}
                    />
                ))}
            </div>
        );
    }
    if (!cell.applicable) {
        return (
            <td
                data-testid={`workforce-review-cell-${employeeId}-${cell.date}`}
                data-applicable="false"
                className="px-2 py-2 text-center text-neutral-400
                            whitespace-nowrap align-top"
            >
                {/* Prompt 18B2 §2 · §3 — absence context is visible
                    even on non-applicable dates for the same
                    employee (multi-day absences · §3). */}
                <div>N/A</div>
                {_absenceChip}
            </td>
        );
    }
    if (!cell.reviewable) {
        return (
            <td
                data-testid={`workforce-review-cell-${employeeId}-${cell.date}`}
                data-applicable="true"
                data-reviewable="false"
                className="group px-2 py-2 whitespace-nowrap align-top"
            >
                {_absenceChip}
            </td>
        );
    }
    const dayCodes = Array.isArray(cell.day_codes) ? cell.day_codes : [];
    const hasShift = Array.isArray(cell.shift_ids) && cell.shift_ids.length > 0;
    const isReviewed = Boolean(cell.reviewed);
    // Prompt 14D §12 · §13 — resolve compact per-cell Rules signal
    // from the canonical rules payload plus this cell's Actual IDs.
    const cellActualIds = new Set(
        (actualEntries || []).filter(
            (a) => a.employee_id === employeeId && a.work_date === cell.date,
        ).map((a) => a.actual_entry_id));
    const cellRules = rulesForCell(
        rulesForEmployee(rules, employeeId), cellActualIds);
    const showRulesHint = cellHasRulesSignal(cellRules);
    // Prompt 18B2-P3 §13-§21 — derive exception-first cell status.
    // GREEN = complete match; RED = complete mismatch or unplanned
    // clock or past plan without clock; AMBER = incomplete clock;
    // NEUTRAL = future or no evidence yet.
    const _hasActual = cell.actual_minutes != null && cell.actual_minutes > 0;
    const _hasClock = Array.isArray(cell.clock_sessions)
        ? cell.clock_sessions.length > 0
        : Boolean(cell.has_clock_evidence);
    const _todayStr = new Date().toISOString().slice(0, 10);
    const _isFuture = cell.date > _todayStr;
    let _status = "neutral";
    if (_hasActual) {
        _status = (cell.difference_minutes === 0) ? "green" : "red";
    } else if (hasShift && _hasClock) {
        if (cell.clock_incomplete) _status = "amber";
        else _status = (cell.difference_minutes === 0) ? "green" : "red";
    } else if (!hasShift && _hasClock) {
        _status = "red";  // Unplanned clock
    } else if (hasShift && !_hasClock && !_isFuture) {
        _status = "red";  // Past plan without clock
    } else if (hasShift && !_hasClock && _isFuture) {
        _status = "neutral";
    } else if (cell.day_off_match) {
        _status = "green";  // Ended published day off, no activity (server-derived)
    }
    // Server-derived clock comparison — same rule as "Approve matching hours".
    const _clockView = _hasActual ? null
        : (_CLOCK_STATUS_VIEW[cell.clock_status]
           || _DAY_OFF_PENDING_VIEW[cell.day_off_pending_reason] || null);
    if (_clockView) _status = _clockView[0];
    // Prompt 18B2-P4 §1 · §2 · §3 · §4 · §5 — a manager-reviewed cell
    // must NOT continue to look like an outstanding red/amber
    // exception. Reviewed = the manager already dealt with it →
    // GREEN / Reviewed. The underlying delta (`difference_minutes`)
    // is UNCHANGED here; it remains rendered on the Δ line so the
    // fact stays visible. Neutral (future / no evidence yet) is
    // NEVER promoted to green — only outstanding attention states
    // are resolved by review.
    if (isReviewed && (_status === "red" || _status === "amber")) {
        _status = "green";
    }
    // Prompt 18B2-P7B §3 · §4 · §16 — Staff Meal completeness is a
    // workflow-relevant signal. A completed worked day
    // with `staff_meal = UNANSWERED` (`staff_meal_missing = true`)
    // is AMBER / "Meal answer missing" even when the time itself
    // has already been reviewed. Once the manager answers YES or
    // NO, this override lifts and the cell falls back to its
    // canonical time-derived status (§16).
    const _mealMissing = Boolean(cell.staff_meal_missing);
    if (_mealMissing) {
        _status = "amber";
    }
    const _statusBg = {
        green: "bg-emerald-50",
        red: "bg-rose-50",
        amber: "bg-amber-50",
        neutral: "",
    }[_status];
    const _baseLabel = {
        green: i18n.t("wf.scheduleGrid.matchesPlan", "Matches plan"),
        red: (!hasShift && _hasClock) ? i18n.t("wf.scheduleGrid.unplannedClock", "Unplanned clock")
             : (hasShift && !_hasClock) ? i18n.t("wf.scheduleGrid.noClockEvidence", "No clock evidence")
             : (hasShift && _hasClock && !_hasActual)
                 ? i18n.t("wf.scheduleGrid.clockRecordedHoursNotEntered", "Clock recorded · hours not entered")
             : i18n.t("wf.scheduleGrid.needsReview", "Needs review"),
        amber: _mealMissing
            ? i18n.t("wf.scheduleGrid.mealAnswerMissing", "Meal answer missing")
            : i18n.t("wf.scheduleGrid.clockIncomplete", "Clock incomplete"),
        neutral: "",
    }[_status];
    // Prompt 18B2-P4 §5 — once reviewed, the GREEN cell speaks the
    // completed-workflow label "Reviewed" instead of "Matches plan"
    // so the manager sees the workflow state, not just the equality.
    const _statusLabel = (isReviewed && _status === "green")
        ? i18n.t("wf.scheduleGrid.reviewed", "Reviewed")
        : (_clockView && !_mealMissing && !isReviewed) ? _clockView[1]
        : _baseLabel;
    // Prompt 18B2-P7B §3 · §20 — compact main-cell Staff Meal
    // indicator. `Meal ✓` (YES) / `No meal` (NO) / `Meal missing`
    // (AMBER · completed worked day + UNANSWERED). Neutral / no
    // evidence days render nothing so the indicator stays subtle.
    let _mealCellLabel = null;
    if (_mealMissing) {
        _mealCellLabel = i18n.t("wf.scheduleGrid.mealMissing", "Meal missing");
    } else if (cell.staff_meal === true) {
        _mealCellLabel = i18n.t("wf.scheduleGrid.mealYes", "Meal ✓");
    } else if (cell.staff_meal === false) {
        _mealCellLabel = i18n.t("wf.scheduleGrid.noMeal", "No meal");
    }
    return (
        <td
            data-testid={`workforce-review-cell-${employeeId}-${cell.date}`}
            data-applicable="true"
            data-reviewable="true"
            data-reviewed={isReviewed ? "true" : "false"}
            data-rules-hint={showRulesHint ? "true" : "false"}
            data-readonly={readOnly ? "true" : undefined}
            data-status={_status}
            className={`group px-2 py-2 whitespace-nowrap text-center align-top ${_statusBg}`}
        >
            <button
                type="button"
                onClick={() => {
                    if (readOnly) return;
                    if (onClick) onClick(employeeId, cell);
                }}
                disabled={readOnly ? true : undefined}
                aria-disabled={readOnly ? "true" : undefined}
                data-testid={`workforce-review-cell-btn-${employeeId}-${cell.date}`}
                className={`w-full text-left rounded px-1 py-0.5
                            hover:bg-neutral-50 focus:outline-none
                            focus-visible:ring-1
                            focus-visible:ring-neutral-400 ${
                    isReviewed
                        ? "border border-neutral-900"
                        : "border border-neutral-200"}`}
            >
                {hasShift ? (
                    <div
                        data-testid={`workforce-review-planned-${employeeId}-${cell.date}`}
                        className="text-[11px] text-neutral-700"
                    >
                        {i18n.t("wf.scheduleGrid.planned", "Planned")} {_fmtDur(cell.planned_minutes)}
                    </div>
                ) : dayCodes.length > 0 ? (
                    <div
                        data-testid={`workforce-review-daycode-${employeeId}-${cell.date}`}
                        className="text-[11px] text-neutral-700"
                    >
                        {dayCodes.join(" · ")}
                    </div>
                ) : null}
                {cell.vv ? <ReviewVVLine employeeId={employeeId} cell={cell} /> : null}
                <div
                    data-testid={`workforce-review-actual-${employeeId}-${cell.date}`}
                    className="text-[11px] text-neutral-700"
                >
                    {i18n.t("wf.scheduleGrid.actual", "Actual")} {_fmtDur(cell.actual_minutes)}
                </div>
                <div
                    data-testid={`workforce-review-diff-${employeeId}-${cell.date}`}
                    className="text-[11px] text-neutral-500"
                >
                    Δ {_fmtDiff(cell.difference_minutes)}
                </div>
                <div
                    data-testid={`workforce-review-status-${employeeId}-${cell.date}`}
                    className="text-[10px] text-neutral-500 mt-0.5"
                >
                    {isReviewed ? i18n.t("wf.scheduleGrid.reviewed", "Reviewed") : i18n.t("wf.scheduleGrid.notReviewed", "Not reviewed")}
                </div>
                {showRulesHint && (
                    <div
                        data-testid={`workforce-review-rules-indicator-${employeeId}-${cell.date}`}
                        className="text-[10px] text-neutral-700 mt-0.5
                                    inline-block rounded-sm border
                                    border-neutral-300 px-1 leading-4"
                    >
                        {i18n.t("wf.scheduleGrid.rules", "Rules")}
                    </div>
                )}
                {_statusLabel ? (
                    <div
                        data-testid={`workforce-review-status-${employeeId}-${cell.date}`}
                        className="text-[10px] text-neutral-700 mt-0.5"
                    >
                        {_statusLabel}
                    </div>
                ) : null}
                {_mealCellLabel ? (
                    <div
                        data-testid={
                            `workforce-review-meal-${employeeId}-${cell.date}`}
                        data-meal-state={
                            _mealMissing ? "missing"
                                : cell.staff_meal === true ? "yes" : "no"}
                        className={`text-[10px] mt-0.5 ${
                            _mealMissing
                                ? "text-amber-800"
                                : "text-neutral-500"}`}
                    >
                        {_mealCellLabel}
                    </div>
                ) : null}
            </button>
            {_absenceChip}
        </td>
    );
}


function ScheduleCell({
    employee,
    cell,
    onShiftCommitIntent,
    onShiftDeleteIntent,
    onPlanCellIntent,
    isShiftCellPending,
    cancelEditVersion,
    openMultiShiftCellKey,
    onMultiOpenChange,
    getTransferContextForEmployee,
    transferRestaurantId,
    readOnly,
    absencesForEmployee,
    vvContext,
    onAbsenceOpen,
    onFillStart,
    fillMark,
}) {
    const employeeId = employee.employee_id;
    const fillAttrs = {
        "data-fill-employee": employeeId,
        "data-fill-date": cell.date,
        "data-fill-target": fillMark ? "true" : undefined,
    };
    const fillCls = fillMark ? " bg-sky-50 ring-1 ring-inset ring-sky-400" : "";
    // Prompt 18B1 §12 · §15 — derive the ACTIVE absences whose
    // inclusive range covers this cell's date. `[]` when none;
    // `null` treated as `[]` safely.
    const cellAbsences = (absencesForEmployee || []).filter(
        (a) => a.start_date <= cell.date && cell.date <= a.end_date);
    // CALENDAR-ABSENCES-V1 — manager actions render beside (not inside) cell editors.
    const absenceActions = typeof onAbsenceOpen === "function";
    const bodyAbsences = absenceActions ? [] : cellAbsences;
    const absenceControls = absenceActions ? (
        <CellAbsenceControls view="schedule" employeeId={employeeId} date={cell.date}
            absences={cellAbsences} hasShift={(cell.shifts || []).length > 0}
            canAdd={!readOnly} onOpen={onAbsenceOpen} />
    ) : null;

    // Prompt 12E §22 · Prompt 12K-3 §7 — N/A representation preserved
    // verbatim; no editor / affordance rendered.
    if (!cell.applicable) {
        return (
            <td
                data-testid={`workforce-schedule-cell-${employeeId}-${cell.date}`}
                data-applicable="false"
                className="px-2 py-2 text-center text-neutral-400
                            whitespace-nowrap"
            >
                N/A
            </td>
        );
    }

    // Prompt 17B2 §6 · §7 — closed-period cells render only the
    // canonical CellBody (Shifts + Day Codes). No editor, no
    // Multi-Shift editor, no per-cell affordance. Read-only display
    // preserves what the manager sees; mutation is prevented at the
    // frontend to avoid a click-then-409 loop.
    if (readOnly) {
        return (
            <td
                data-testid={`workforce-schedule-cell-${employeeId}-${cell.date}`}
                data-applicable="true"
                data-readonly="true"
                className="group px-2 py-2 text-center whitespace-nowrap
                            text-neutral-800 align-top"
            >
                <CellBody employeeId={employeeId} cell={cell} absences={bodyAbsences} />
                {absenceControls}
            </td>
        );
    }

    const pending = typeof isShiftCellPending === "function"
        ? Boolean(isShiftCellPending(employeeId, cell.date))
        : false;

    const shiftCount = Array.isArray(cell.shifts) ? cell.shifts.length : 0;
    const dayEntries = Array.isArray(cell.day_entries) ? cell.day_entries : [];
    const dayEntryCount = dayEntries.length;
    const cellKey = _cellKey(employeeId, cell.date);
    const multiOpen = openMultiShiftCellKey === cellKey;

    // Prompt 12I-2 §31 · §32 — intent forwarding for both direct and
    // multi-editor mutations goes through the SAME parent handlers.
    const forwardCommit = (intent) => {
        if (typeof onShiftCommitIntent === "function") {
            onShiftCommitIntent(employeeId, cell.date, intent);
        }
    };
    const forwardDelete = (intent) => {
        if (typeof onShiftDeleteIntent === "function") {
            onShiftDeleteIntent(employeeId, cell.date, intent);
        }
    };
    // Prompt 12K-3 §10 · §11 · §12 · §41 — canonical K2 intent
    // forwarding attaches ONLY (employee_id, date) cell context and
    // hands the raw intent (preserving canonical dayEntry when
    // present) to WorkforceSchedule.
    const forwardPlanCell = (intent) => {
        if (typeof onPlanCellIntent === "function") {
            onPlanCellIntent(employeeId, cell.date, intent);
        }
    };

    // Prompt 12I-2 §29 — cannot start a NEW advanced editing flow on
    // a pending closed cell. Already-open MultiShiftEditor is allowed
    // to remain visible so its active-row Saving… stays informative.
    const handleMultiOpenRequest = (nextOpen) => {
        if (nextOpen && pending && !multiOpen) return;
        onMultiOpenChange(cellKey, nextOpen);
    };

    // Prompt 12K-3 §5 · §33 · §34 · §35 · §36 — LEGACY_INVALID
    // presentation. A cell is legacy-invalid if it holds ANY combined
    // occupancy that violates atomic exclusivity: shifts AND day
    // entries, OR more than one day entry. Do NOT auto-repair; render
    // both occupancy types visibly and expose explicit per-Day-Entry
    // remove actions.
    const isLegacyInvalid = (
        (shiftCount > 0 && dayEntryCount > 0)
            || dayEntryCount >= 2
    );

    if (isLegacyInvalid) {
        return (
            <td
                data-testid={`workforce-schedule-cell-${employeeId}-${cell.date}`}
                data-applicable="true"
                data-legacy-invalid="true"
                {...fillAttrs}
                data-pending={pending ? "true" : undefined}
                className={"group px-2 py-2 text-center whitespace-nowrap text-neutral-800 align-top" + fillCls}
            >
                <CellBody employeeId={employeeId} cell={cell} absences={bodyAbsences} />
                <div
                    data-testid={`workforce-schedule-legacy-actions-${employeeId}-${cell.date}`}
                    className="mt-1 flex flex-col items-start gap-0.5"
                >
                    {dayEntries.map((d) => (
                        <button
                            key={d.day_entry_id}
                            type="button"
                            aria-label={`Remove day code ${d.code}`}
                            disabled={pending}
                            onClick={() => forwardPlanCell({
                                mode: "delete-day-entry",
                                dayEntry: d,
                                source: "legacy-remove",
                            })}
                            data-testid={`workforce-schedule-legacy-remove-${d.day_entry_id}`}
                            className="text-[10px] leading-4
                                        text-neutral-500 underline
                                        hover:text-neutral-800
                                        disabled:opacity-50"
                        >
                            {i18n.t("wf.scheduleGrid.removeDayCode", "Remove day code {{code}}", { code: d.code })}
                        </button>
                    ))}
                </div>
                {pending && (
                    <div
                        data-testid={`workforce-schedule-cell-saving-${employeeId}-${cell.date}`}
                        className="text-[10px] leading-4 text-neutral-500 mt-0.5"
                    >
                        {i18n.t("common.saving", "Saving…")}
                    </div>
                )}
                {absenceControls}
            </td>
        );
    }

    // Prompt 12K-3 §9 · §22 · §23 · §24 — DAY_ENTRY-only cell renders
    // the K2 DayEntryCellEditor. Pending cells fall back to a
    // read-only body so a second mutation cannot be requested for the
    // same cell (§18).
    if (shiftCount === 0 && dayEntryCount === 1) {
        // Prompt 16B-2B1 §3 · §5 — expose Transferred-X popover on X
        // Day Entries when the employee is AVERAGING/TES12_1_STANDARD.
        const singleDe = dayEntries[0];
        const transferCtx =
            typeof getTransferContextForEmployee === "function"
                ? getTransferContextForEmployee(employeeId)
                : null;
        const showTransferControl = Boolean(
            !pending
                && singleDe.code === "X"
                && transferCtx
                && transferCtx.cycle
                && transferCtx.cycle.averaging_cycle_id);
        return (
            <td
                data-testid={`workforce-schedule-cell-${employeeId}-${cell.date}`}
                data-applicable="true"
                data-day-entry="true"
                {...fillAttrs}
                data-pending={pending ? "true" : undefined}
                className={"group px-2 py-2 text-center whitespace-nowrap text-neutral-800 align-top" + fillCls}
            >
                {pending ? (
                    <CellBody employeeId={employeeId} cell={cell} absences={bodyAbsences} />
                ) : (
                    <DayEntryCellEditor
                        cell={cell}
                        cellKey={cellKey}
                        cancelEditVersion={cancelEditVersion}
                        isPending={pending}
                        onIntent={forwardPlanCell}
                    >
                        <CellBody employeeId={employeeId} cell={cell} absences={bodyAbsences} />
                    </DayEntryCellEditor>
                )}
                {showTransferControl && (
                    <TransferredXControl
                        restaurantId={transferRestaurantId}
                        dayEntry={singleDe}
                        cycle={transferCtx.cycle}
                        onIntent={forwardPlanCell}
                        isPending={pending}
                    />
                )}
                {pending && (
                    <div
                        data-testid={`workforce-schedule-cell-saving-${employeeId}-${cell.date}`}
                        className="text-[10px] leading-4 text-neutral-500 mt-0.5"
                    >
                        {i18n.t("common.saving", "Saving…")}
                    </div>
                )}
                {absenceControls}
            </td>
        );
    }

    // Prompt 12I-2 §29 · Prompt 12G-3 §13 · Prompt 12K-3 §8 —
    // Multi-Shift cell (2+ Shifts, 0 Day Entries): existing MultiShift
    // editor behavior preserved verbatim.
    if (shiftCount >= 2) {
        return (
            <td
                data-testid={`workforce-schedule-cell-${employeeId}-${cell.date}`}
                data-applicable="true"
                data-multi-shift="true"
                {...fillAttrs}
                data-pending={pending ? "true" : undefined}
                className={"group px-2 py-2 text-center whitespace-nowrap text-neutral-800 align-top" + fillCls}
            >
                <MultiShiftEditor
                    open={multiOpen}
                    onOpenChange={handleMultiOpenRequest}
                    trigger={
                        <button
                            type="button"
                            aria-label={i18n.t("wf.scheduleGrid.manageShifts", "Manage shifts")}
                            data-testid={`workforce-schedule-manage-shifts-${employeeId}-${cell.date}`}
                            className="block w-full text-center
                                        rounded hover:bg-neutral-50
                                        focus:outline-none
                                        focus-visible:ring-1
                                        focus-visible:ring-neutral-400"
                        >
                            <CellBody employeeId={employeeId} cell={cell} absences={bodyAbsences} />
                        </button>
                    }
                    employee={employee}
                    cell={cell}
                    cancelEditVersion={cancelEditVersion}
                    isPending={pending}
                    onCommitIntent={forwardCommit}
                    onDeleteIntent={forwardDelete}
                />
                {pending && (
                    <div
                        data-testid={`workforce-schedule-cell-saving-${employeeId}-${cell.date}`}
                        className="text-[10px] leading-4 text-neutral-500 mt-0.5"
                    >
                        {i18n.t("common.saving", "Saving…")}
                    </div>
                )}
                {absenceControls}
            </td>
        );
    }

    // Prompt 12K-3 §6 · §8 — 0 or 1 Shift with 0 Day Entries.
    // EMPTY applicable cell → PlanCellEditor (K2) so V/X/TS/VV OR a
    // Shift time (8-16) can be entered through one editor. Existing
    // 1-Shift ShiftCellEditor + MultiShiftEditor •••  affordance is
    // preserved verbatim for the 1-Shift case. Day Entry codes are
    // NEVER accepted in an occupied Shift cell (§8).
    return (
        <td
            data-testid={`workforce-schedule-cell-${employeeId}-${cell.date}`}
            data-applicable="true"
            data-plan-cell={shiftCount === 0 ? "true" : undefined}
                {...fillAttrs}
            data-pending={pending ? "true" : undefined}
            className={"group relative px-2 py-2 text-center whitespace-nowrap text-neutral-800 align-top" + fillCls}
        >
            {pending ? (
                <CellBody employeeId={employeeId} cell={cell} absences={bodyAbsences} />
            ) : shiftCount === 0 ? (
                <PlanCellEditor
                    cell={cell}
                    cellKey={cellKey}
                    cancelEditVersion={cancelEditVersion}
                    isPending={pending}
                    onIntent={forwardPlanCell}
                    vvContext={vvContext}
                >
                    <CellBody employeeId={employeeId} cell={cell} absences={bodyAbsences} />
                </PlanCellEditor>
            ) : (
                <ShiftCellEditor
                    cell={cell}
                    cellKey={cellKey}
                    cancelEditVersion={cancelEditVersion}
                    onCommitIntent={forwardCommit}
                    onDeleteIntent={forwardDelete}
                >
                    <CellBody employeeId={employeeId} cell={cell} absences={bodyAbsences} />
                </ShiftCellEditor>
            )}
            {pending && (
                <div
                    data-testid={`workforce-schedule-cell-saving-${employeeId}-${cell.date}`}
                    className="text-[10px] leading-4 text-neutral-500 mt-0.5"
                >
                    {i18n.t("common.saving", "Saving…")}
                </div>
            )}
            {shiftCount === 1 && (
                <MultiShiftEditor
                    open={multiOpen}
                    onOpenChange={handleMultiOpenRequest}
                    trigger={
                        <button
                            type="button"
                            aria-label={i18n.t("wf.scheduleGrid.manageShifts", "Manage shifts")}
                            onClick={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                            onKeyDown={(e) => e.stopPropagation()}
                            data-testid={`workforce-schedule-manage-shifts-${employeeId}-${cell.date}`}
                            className={pending
                                ? "hidden"
                                : "opacity-0 group-hover:opacity-100 "
                                    + "focus-visible:opacity-100 "
                                    + "group-focus-within:opacity-100 "
                                    + "absolute right-1 top-1 rounded "
                                    + "p-0.5 text-neutral-500 "
                                    + "hover:text-neutral-800 "
                                    + "focus:outline-none "
                                    + "focus-visible:ring-1 "
                                    + "focus-visible:ring-neutral-400"}
                        >
                            <MoreHorizontal
                                className="h-3.5 w-3.5"
                                strokeWidth={2}
                            />
                        </button>
                    }
                    employee={employee}
                    cell={cell}
                    cancelEditVersion={cancelEditVersion}
                    isPending={pending}
                    onCommitIntent={forwardCommit}
                    onDeleteIntent={forwardDelete}
                />
            )}
            {shiftCount === 1 && dayEntryCount === 0 && !pending && onFillStart && (
                <FillHandle employeeId={employeeId} date={cell.date} onFillStart={onFillStart} />
            )}
            {absenceControls}
        </td>
    );
}

// Drag-fill handle: drag across the same row to copy this Shift.
function FillHandle({ employeeId, date, onFillStart }) {
    const stop = (e) => e.stopPropagation();
    return (
        <span
            role="button"
            aria-label={i18n.t("wf.scheduleGrid.dragToCopyShift", "Drag to copy shift")}
            title={i18n.t("wf.scheduleGrid.dragToCopyThisShiftTo", "Drag to copy this shift to other days")}
            data-testid={`workforce-schedule-fill-handle-${employeeId}-${date}`}
            onPointerDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onFillStart(employeeId, date);
            }}
            onMouseDown={stop}
            onClick={stop}
            className="absolute bottom-0.5 right-0.5 h-2.5 w-2.5 cursor-crosshair rounded-sm
                        bg-sky-600 opacity-0 group-hover:opacity-100 transition-opacity
                        [touch-action:none]"
        />
    );
}
